import {
	BrowserCacheLocation,
	EventType,
	PublicClientApplication,
} from "@azure/msal-browser";

export const useMsalStore = defineStore("msal", () => {
	const config = useRuntimeConfig();
	const { getAccount } = useClubCirqueApi();
	const { clear, loggedIn } = useUserSession();

	const msalInstance = ref(null);
	const isInitialized = ref(false);

	const auth = ref(null);
	const token = computed(() => auth.value?.accessToken);
	const localAccountId = computed(() => auth.value?.account?.localAccountId);
	const firstName = computed(() => auth.value?.idTokenClaims?.given_name);
	const lastName = computed(() => auth.value?.idTokenClaims?.family_name);
	const email = computed(() => auth.value?.idTokenClaims?.email);

	async function initialize() {
		if (isInitialized.value) {
			return;
		}

		try {
			const redirectUri = `${window.location.origin}/auth`;
			const logoutRedirectUri = `${window.location.origin}/club-cirque`;

			msalInstance.value = new PublicClientApplication({
				auth: {
					clientId: config.public.azureB2CClientId,
					authority: config.public.azureB2CAuthority,
					knownAuthorities: config.public.azureB2CKnownAuthorities,
					redirectUri: redirectUri,
					postLogoutRedirectUri: logoutRedirectUri,
				},
				cache: {
					cacheLocation: BrowserCacheLocation.LocalStorage,
					storeAuthStateInCookie: true,
				},
			});

			await msalInstance.value.initialize();

			if (!loggedIn.value || !token.value) {
				await acquireTokenSilent();
			}

			isInitialized.value = true;
		} catch (err) {
			console.error("[useAuth] - Initialization error:", err);
		}
	}

	// Sign in the user
	async function signIn() {
		try {
			await msalInstance.value
				.loginPopup({
					scopes: config.public.azureB2CScopes,
				})
				.then(handleResponse);
		} catch (err) {
			console.error("[useAuth] - Login error:", err);
		}
	}

	// Check if user is authenticated
	function isAuthenticated() {
		return msalInstance.value.getAllAccounts().length > 0;
	}

	// Sign out user
	async function signOut(accountId = null) {
		const account = accountId
			? msalInstance.value.getAccountByHomeId(accountId)
			: null;
		const logoutOptions = account ? { account } : {};
		try {
			await msalInstance.value.logoutPopup(logoutOptions).then(handleResponse);
			reloadNuxtApp();
		} catch (err) {
			await clearSession();
			console.error("[useAuth] - Logout error:", err);
		}
	}

	// Handle redirect response
	async function handleResponse(resp = null) {
		if (resp !== null && resp.account) {
			setAuth(resp);
			return getAccount();
		} else {
			await clearSession();
			return true;
		}
	}

	// Clear session and local storage
	async function clearSession() {
		setAuth(null);
		localStorage.clear();
		await clear();
	}

	// Acquire access token silently
	async function acquireTokenSilent() {
		const accounts = msalInstance.value.getAllAccounts();
		if (accounts.length > 0) {
			const account = accounts[0];
			msalInstance.value.setActiveAccount(account);
			try {
				await msalInstance.value
					.acquireTokenSilent({
						account,
						scopes: config.public.azureB2CScopes,
					})
					.then(handleResponse);
			} catch (err) {
				console.error("[useAuth] - Acquire Token Silent error:", err);
				await clearSession();
			}
		}
	}

	function setAuth(a = null) {
		auth.value = a;
	}

	return {
		initialize,
		signIn,
		isAuthenticated,
		acquireTokenSilent,
		signOut,
		localAccountId,
		firstName,
		lastName,
		email,
		token,
	};
});
